import React, { useMemo, useState, useCallback } from 'react';
import { Copy, Check, ArrowDown, ArrowUp, X } from 'lucide-react';

const formatNumber = (num) => num ? new Intl.NumberFormat().format(num) : '-';

const ComparisonResults = ({ 
  results = [],
  market1 = 'jita',
  market2 = 'ualx',
  sellTotalMarket1 = 0,
  sellTotalMarket2 = 0,
}) => {
  const [copiedMarket, setCopiedMarket] = useState(null);
  const [market1ShippingRate, setMarket1ShippingRate] = useState(850);
  const [market2ShippingRate, setMarket2ShippingRate] = useState(0);

  // Process results with shipping costs
  const processedResults = useMemo(() => (
    results.map(item => ({
      ...item,
      sell_price_market1: item.sell_price_market1 
        ? item.sell_price_market1 + (item.item_vol * market1ShippingRate)
        : item.sell_price_market1,
      sell_price_market2: item.sell_price_market2
        ? item.sell_price_market2 + (item.item_vol * market2ShippingRate)
        : item.sell_price_market2
    }))
  ), [results, market1ShippingRate, market2ShippingRate]);

  // Market list processing
  const { market1Items, market2Items, market1Total, market2Total } = useMemo(() => {
    let m1Total = 0, m2Total = 0;
    const m1Items = [], m2Items = [];

    processedResults.forEach(item => {
      const price1 = item.sell_price_market1 || 0;
      const price2 = item.sell_price_market2 || 0;

      if (price1 > 0 && price2 > 0) {
        // Both markets have prices
        if (price1 <= price2) { // Using <= instead of < to handle equal prices
          m1Items.push(item);
          m1Total += price1 * item.quantity;
        } else {
          m2Items.push(item);
          m2Total += price2 * item.quantity;
        }
      } else if (price1 > 0) {
        // Only market1 has price
        m1Items.push(item);
        m1Total += price1 * item.quantity;
      } else if (price2 > 0) {
        // Only market2 has price
        m2Items.push(item);
        m2Total += price2 * item.quantity;
      }
    });

    return { market1Items: m1Items, market2Items: m2Items, market1Total: m1Total, market2Total: m2Total };
  }, [processedResults]);

  // Copy handler for market lists
  const handleCopyMarketList = (items, marketName) => {
    const copyText = items
      .map(item => `${item.name} x${item.quantity}`)
      .join('\n');
    
    navigator.clipboard.writeText(copyText).then(() => {
      setCopiedMarket(marketName);
      setTimeout(() => setCopiedMarket(null), 2000);
    });
  };

  return (
    <div className="comparison-results font-mono bg-zinc-900 rounded-lg shadow-lg p-4">
      <h3 className="text-xl font-semibold font-mono mb-4 text-orange-300">
        Shopping List - {market1.toUpperCase()} vs {market2.toUpperCase()}
      </h3>
      <p className="font-mono font-xs mb-4 text-gray-400">Always double check prices/volume</p>
      <div className="grid grid-cols-1 font-mono md:grid-cols-2 gap-4 mb-8">
        <MarketColumn 
          market={market1}
          items={market1Items}
          total={market1Total}
          shippingRate={market1ShippingRate}
          onCopy={() => handleCopyMarketList(market1Items, market1)}
          isCopied={copiedMarket === market1}
          market1={market1}
          onShippingRateChange={(value) => setMarket1ShippingRate(value)}
        />
        <MarketColumn 
          market={market2}
          items={market2Items}
          total={market2Total}
          shippingRate={market2ShippingRate}
          onCopy={() => handleCopyMarketList(market2Items, market2)}
          isCopied={copiedMarket === market2}
          market1={market1}
          onShippingRateChange={(value) => setMarket2ShippingRate(value)}
        />
      </div>

      <RecommendationTable items={processedResults} market1={market1} market2={market2} />
    </div>
  );
};

const ItemImage = ({ itemId, name }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  return (
    <div className="item_icon" style={{ position: 'relative' }}>
      {!imageLoaded && (
        <img
          className="item_icon"
          src="/items.png"
          alt="Item placeholder"
          style={{ position: 'absolute' }}
        />
      )}
      <img 
        className="item_icon"
        style={{ opacity: imageLoaded ? 1 : 0, transition: 'opacity 0.2s' }}
        src={`https://imageserver.eveonline.com/Type/${itemId}_64.png`}
        alt={name || 'Item icon'}
        loading="lazy"
        onLoad={handleImageLoad}
      />
    </div>
  );
};


// Market Column Component
const MarketColumn = ({ market, items, total, shippingRate, onCopy, isCopied, market1, onShippingRateChange }) => {
  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -10 : 10;
    const newValue = Math.max(0, shippingRate + delta);
    onShippingRateChange(newValue);
  };

  const handleFocus = () => {
    document.body.style.overflow = 'hidden';
  };

  const handleBlur = () => {
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="bg-zinc-800 font-mono rounded-lg p-4">
      <div className="flex justify-between font-mono items-center mb-4">
        <div className="flex items-center font-mono gap-2">
          <h4 className="text-lg font-semibold font-mono text-orange-300">
            {market.toUpperCase()}
          </h4>
          <button 
            onClick={onCopy}
            className="p-1 hover:bg-zinc-700 font-mono rounded transition-colors"
            title="Copy item list"
          >
            {isCopied ? (
              <Check className="text-green-500" size={18} />
            ) : (
              <Copy className="text-gray-400" size={18} />
            )}
          </button>
        </div>
        <div className="text-sm text-gray-400 font-mono">
          {formatNumber(total)} ISK
          {shippingRate > 0 && (
            <div className="text-xs font-mono mt-1">Includes shipping</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <label className="text-gray-400 text-sm font-mono">
          Shipping (ISK/m³):
        </label>
        <input
          type="number"
          value={shippingRate}
          onChange={(e) => onShippingRateChange(Number(e.target.value) || 0)}
          onWheel={handleWheel}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="bg-zinc-900 text-orange-300 p-1 rounded w-14 font-mono [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
           border border-gray-600 hover:border-orange-400  focus:ring-1 focus:ring-orange-500 focus:outline-none"
          min="0"
        />
      </div>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="bg-zinc-900 p-2 rounded flex font-mono justify-between items-center">
            <div className="flex items-center">
              {item.item_id && (
                <ItemImage itemId={item.item_id} name={item.name} />
              )}
              <span className="font-medium font-mono ml-2">{item.name}</span>
              <span className="text-xs font-mono text-gray-400 ml-2">
                x{item.quantity} ({formatNumber(item.total_item_vol)}m³)
              </span>
            </div>
            <div className="text-orange-300 font-mono text-sm">
              {formatNumber(market === market1 ? item.sell_price_market1 * item.quantity : item.sell_price_market2 * item.quantity)} ISK
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Recommendation Table Component
const RecommendationTable = ({ items, market1, market2 }) => {
  const recommendations = useMemo(() => items, [items]);

  return (
    <div className="bg-zinc-800 rounded-lg p-4 font-mono">
      <h4 className="text-lg font-semibold mb-4 text-orange-300 font-mono">Price Comparison</h4>
      <table className="w-full">
        <thead className="bg-zinc-900">
          <tr>
            <th> </th>
            <th className="p-2 text-left text-orange-300 font-mono">Item</th>
            <th className="p-2 text-left text-orange-300 font-mono">{market1.toUpperCase()}</th>
            <th className="p-2 text-left text-orange-300 font-mono">{market2.toUpperCase()}</th>
            <th className="p-2 text-left text-orange-300 font-mono">Best Market</th>
          </tr>
        </thead>
        <tbody>
          {recommendations.map((item, index) => {
            const price1 = item.sell_price_market1 || 0;
            const price2 = item.sell_price_market2 || 0;
            
            // Determine best market considering availability
            let bestMarket;
            if (price1 === 0 && price2 === 0) {
              bestMarket = 'N/A';
            } else if (price1 === 0) {
              bestMarket = market2;
            } else if (price2 === 0) {
              bestMarket = market1;
            } else {
              bestMarket = price1 <= price2 ? market1 : market2; // Using <= to show market1 when prices are equal
            }

            return (
              <tr key={index} className="even:bg-zinc-900/50 font-mono">
                <td>
                  {item.item_id && (
                    <ItemImage itemId={item.item_id} name={item.name} />
                  )}
                </td>
                <td className="p-2 text-left font-mono">{item.name}</td>
                <td className="p-2 text-left font-mono">
                  <PriceCell 
                    price={price1} 
                    isBest={bestMarket === market1 && price1 > 0}
                  />
                </td>
                <td className="p-2 text-left font-mono">
                  <PriceCell 
                    price={price2} 
                    isBest={bestMarket === market2 && price2 > 0}
                  />
                </td>
                <td className="p-2 flex text-right items-center gap-1 font-mono">
                  {bestMarket === 'N/A' || price2 === 0 ? (
                    'N/A'
                  ) : (
                    <>
                      {((price1/price2)*100).toFixed(2)}%
                      {(price1 > 0 && price2 > 0) && (
                        bestMarket === market1 ? (
                          <ArrowDown className="text-green-500" size={16} />
                        ) : (
                          <ArrowUp className="text-red-500" size={16} />
                        )
                      )}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Price Cell Component (updated to handle zero prices)
const PriceCell = ({ price, isBest }) => (
  <div className={`flex items-center text-right gap-1 ${isBest ? 'text-green-500' : 'text-gray-400'}`}>
    {price > 0 ? formatNumber(price) : <span className="text-red-500">Not Available</span>}
    {isBest && <ArrowDown className="inline-block" size={14} />}
  </div>
);

export default ComparisonResults;
